import { useState } from 'react';
import type { Command, PlayerId, SimState } from '@/game/sim';
import { ungroupCommand } from '@/game/view/groups';
import { type RosterTarget, rosterMenuItems, slotGrid } from '@/game/view/rosterMenu';
import { assignSlot } from '@/game/view/slots';
import { ContextMenu, MenuItem } from './ContextMenu';
import { startRename, type UiStore } from './ui/store';

/**
 * The Units roster's right-click menu for a unit row or group heading:
 * Rename, Assign to slot (a 1-9 grid; a taken slot shows its head count and
 * is replaced on click), Ungroup, Go to and Select. Slots are the client-local
 * control groups: assigning never touches formations.
 */
export function RosterMenu({
  x,
  y,
  target,
  title,
  state,
  playerId,
  ui,
  onFocusUnit,
  onSelectMany,
  dispatch,
  onClose,
}: {
  x: number;
  y: number;
  target: RosterTarget;
  /** The target's display name, for the menu's accessible name. */
  title: string;
  state: SimState;
  playerId: PlayerId;
  ui: UiStore;
  onFocusUnit: (id: string) => void;
  onSelectMany: (ids: string[], additive: boolean) => void;
  dispatch: (command: Command) => void;
  onClose: () => void;
}) {
  const [slotsOpen, setSlotsOpen] = useState(false);
  const cells = slotGrid(state, playerId, ui.get().slots, target.unitIds);

  function run(action: string) {
    if (action === 'slot') {
      setSlotsOpen((open) => !open);
      return;
    }
    onClose();
    if (action === 'rename') startRename(ui, target.id, 'row');
    else if (action === 'goto') onFocusUnit(target.unitIds[0]);
    else if (action === 'select') onSelectMany([...target.unitIds], false);
    else if (action === 'ungroup') {
      const command = ungroupCommand(state, playerId, target.unitIds);
      if (command) dispatch(command);
    }
  }

  return (
    <ContextMenu x={x} y={y} label={`${title} actions`} onClose={onClose}>
      {rosterMenuItems(state, target).map((item) => (
        <div key={item.action}>
          <MenuItem
            hint={item.hint}
            expanded={item.action === 'slot' ? slotsOpen : undefined}
            onClick={() => run(item.action)}
          >
            {item.label}
            {item.action === 'slot' ? ' ▸' : ''}
          </MenuItem>
          {item.action === 'slot' && slotsOpen && (
            <div className="grid grid-cols-3 gap-1 px-1 pb-1 pt-0.5">
              {cells.map((c) => (
                <button
                  key={c.n}
                  type="button"
                  role="menuitem"
                  aria-label={`Slot ${c.n}${c.state === 'empty' ? '' : c.state === 'current' ? ', holds these units' : `, ${c.count} units, replace`}`}
                  title={c.state === 'used' ? `Slot ${c.n} holds ${c.count}: replace` : undefined}
                  onClick={() => {
                    const slots = ui.get().slots;
                    ui.set({ slots: assignSlot(slots, c.n, state, playerId, target.unitIds) });
                    onClose();
                  }}
                  className={`btn h-7 flex-col gap-0 px-0 text-[11px] leading-none ${c.state === 'current' ? 'btn-primary' : 'btn-secondary'} ${c.state === 'used' ? 'opacity-60' : ''}`}
                >
                  <span>{c.n}</span>
                  {c.state !== 'empty' && (
                    <span className="text-[8px] tabular-nums">{c.count}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
    </ContextMenu>
  );
}
